import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { toast } from "sonner";

const LIST_KINDS = [
  ["asset_classes", "Asset classes", "One per line"],
  ["ownership_forms", "Ownership forms", "One per line"],
];

export default function WealthConfiguration() {
  const [config, setConfig] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => setTenants([]));
  }, []);

  const demo = async (t, load) => {
    try {
      if (load) await api.post(`/wealth-config/demo-data/${t.id}`);
      else await api.delete(`/wealth-config/demo-data/${t.id}`);
      toast.success(`${t.name}: Ramanathan demo data ${load ? "loaded" : "cleared"}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = () =>
    api.get("/wealth-config").then(({ data }) => {
      setConfig(data);
      setDrafts({
        asset_classes: (data.asset_classes.items || []).join("\n"),
        ownership_forms: (data.ownership_forms.items || []).join("\n"),
        liquidity_bands: JSON.stringify(data.liquidity_bands.items, null, 2),
        multiple_profile: JSON.stringify(data.multiple_profile.items, null, 2),
        stale_thresholds_days: JSON.stringify(data.stale_thresholds_days.items, null, 2),
      });
    }).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { load(); }, []);

  const publish = async (kind, items) => {
    try {
      await api.put(`/wealth-config/${kind}`, { items });
      toast.success(`Published a new version of ${kind.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const publishList = (kind) =>
    publish(kind, drafts[kind].split("\n").map((s) => s.trim()).filter(Boolean));

  const publishJson = (kind) => {
    try {
      publish(kind, JSON.parse(drafts[kind]));
    } catch {
      toast.error("That is not valid JSON");
    }
  };

  if (!config) return <p className="label-caps animate-pulse">Loading configuration</p>;

  return (
    <div className="space-y-10">
      <PageHeader
        testid="wealth-config-header"
        eyebrow="Administration · Viveka Kartha"
        title="Wealth framework configuration"
        description="Aikyam-controlled lists and bands for the Wealth Allocation module. Publishing creates a new immutable version — historical wealth positions keep the version they were approved with."
      />

      <div className="panel p-6" data-testid="wealth-demo-data">
        <h2 className="font-display text-lg text-navy">Illustrative demo data</h2>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Load the Ramanathan Family illustrative dataset into any engagement to demonstrate the
          framework. Needed once in each environment (the published app has its own database).
        </p>
        <div className="mt-4 divide-y divide-border">
          {tenants.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 py-3"
                 data-testid={`wealth-demo-row-${t.id}`}>
              <p className="text-sm font-medium text-navy">{t.name}</p>
              <div className="flex gap-2">
                <button onClick={() => demo(t, true)} data-testid={`wealth-demo-load-${t.id}`}
                        className="rounded-full bg-navy px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-navy/90">
                  Load demo data
                </button>
                <button onClick={() => demo(t, false)} data-testid={`wealth-demo-clear-${t.id}`}
                        className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-navy hover:bg-muted">
                  Clear
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {LIST_KINDS.map(([kind, label, hint]) => (
          <div key={kind} className="panel p-6" data-testid={`wealth-config-${kind}`}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-display text-lg text-navy">{label}</h2>
              <span className="label-caps">{config[kind].version_label}</span>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
            <textarea rows={12} value={drafts[kind] || ""} data-testid={`wealth-config-input-${kind}`}
                      onChange={(e) => setDrafts({ ...drafts, [kind]: e.target.value })}
                      className="mt-3 w-full resize-y rounded-xl border border-border bg-card px-4 py-3 font-mono text-xs outline-none focus:border-plum" />
            <button onClick={() => publishList(kind)} data-testid={`wealth-config-publish-${kind}`}
                    className="mt-3 rounded-full bg-plum px-5 py-2.5 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
              Publish new version
            </button>
          </div>
        ))}

        {[["liquidity_bands", "Liquidity bands"], ["multiple_profile", "Score-to-multiple profile"],
          ["stale_thresholds_days", "Stale valuation thresholds (days)"]].map(([kind, label]) => (
          <div key={kind} className="panel p-6" data-testid={`wealth-config-${kind}`}>
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-display text-lg text-navy">{label}</h2>
              <span className="label-caps">{config[kind].version_label}</span>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              JSON. {kind === "multiple_profile" && "Used by the Business Value Engine from Phase 3; Panchatanthra 0-5 scores are converted ×2 to 0-10."}
            </p>
            <textarea rows={12} value={drafts[kind] || ""} data-testid={`wealth-config-input-${kind}`}
                      onChange={(e) => setDrafts({ ...drafts, [kind]: e.target.value })}
                      className="mt-3 w-full resize-y rounded-xl border border-border bg-card px-4 py-3 font-mono text-xs outline-none focus:border-plum" />
            <button onClick={() => publishJson(kind)} data-testid={`wealth-config-publish-${kind}`}
                    className="mt-3 rounded-full bg-plum px-5 py-2.5 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
              Publish new version
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
